/**
 * Bureau Report API
 * Fetches Equifax credit report via the integrations service.
 *
 * NOTE: Does NOT extend BaseApi — BaseApi's request interceptor overwrites the
 * Authorization header with the agent token, while this endpoint needs a
 * static integrations JWT (REACT_APP_BUREAU_AUTH_TOKEN).
 */

import axios from 'axios';
import { INTEGRATIONS_API_BASE_URL } from '@/config';
import type {
    GenerateBureauReportRequest,
    GenerateBureauReportResponse,
    BureauReportData,
} from '@/types/bureau.types';

// sessionStorage → cached copy survives refreshes but is cleared when the tab
// closes, so we never re-fetch the same user's report within a tab session.
const cacheKey = (userId: string) => `bureau_report_${userId}`;

export const getCachedBureauReport = (userId: string): BureauReportData | null => {
    try {
        const raw = sessionStorage.getItem(cacheKey(userId));
        return raw ? (JSON.parse(raw) as BureauReportData) : null;
    } catch {
        return null;
    }
};

const setCachedBureauReport = (userId: string, data: BureauReportData): void => {
    try {
        sessionStorage.setItem(cacheKey(userId), JSON.stringify(data));
    } catch {
        // Quota/serialization issues shouldn't break the flow.
    }
};

export class BureauApi {
    /**
     * Generate (or fetch cached) bureau report for a user.
     * Returns the inner report payload (response.data.data).
     */
    async generateBureauReport(userId: string): Promise<BureauReportData> {
        const token = process.env.REACT_APP_BUREAU_AUTH_TOKEN || '';
        const request: GenerateBureauReportRequest = {
            tag: 'equifax',
            user_id: userId,
        };

        try {
            const response = await axios.post<GenerateBureauReportResponse>(
                `${INTEGRATIONS_API_BASE_URL}/generate_bureau_report`,
                request,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: '*/*',
                        Authorization: token,
                    },
                    timeout: 60000, // bureau pulls can be slow
                }
            );

            const payload = response.data?.data;
            if (payload?.success && payload.data) {
                setCachedBureauReport(userId, payload.data);
                return payload.data;
            }

            throw new Error(payload?.message || 'Failed to fetch credit details');
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const serverMessage = (
                    error.response?.data as { data?: { message?: string }; message?: string }
                )?.data?.message || (error.response?.data as { message?: string })?.message;
                throw new Error(
                    serverMessage || error.message || 'Failed to fetch credit details'
                );
            }
            throw error;
        }
    }
}

// Singleton instance
export const bureauApi = new BureauApi();
